import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { DataTransformService, RelatedCard, Ticket, TransferTicketEmailReceiverVerify } from '@ridango/core';
import { USE_ANONYMOUS_ACCESS_TOKEN } from '@ridango/core/lib/services/auth.service';

import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { ProductsService } from '../products/products.service';

@Injectable({
    providedIn: 'root'
})
export class AccountTicketsService {
    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private dataTransformer: DataTransformService,
        private productsService: ProductsService
    ) {}

    fetchTicketsByRelatedCard(card: RelatedCard, companyId = null): Observable<Ticket[]> {
        let url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/related-card/${card.id}`;
        if (companyId && parseInt(companyId, 10)) {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${card.id}/company/${companyId}/tickets`;
        }
        return this.http.get(url).pipe(
            switchMap((d: Ticket[]) => {
                const ticketsWithTranslations = d.map((t: Ticket) => {
                    if (!t.product) {
                        t.product = { translations: [] };
                    } else if (!t.product.translations) {
                        t.product.translations = [];
                    }
                    t.product.name = t.name;
                    t.product.translations = t.product.translations.concat(
                        this.dataTransformer.transformObjectTranslations(t, 'name')
                    );
                    if (t.trip_messages) {
                        t.trip_messages.forEach((m) => {
                            m.translations = [];
                            m.translations = this.dataTransformer.transformObjectTranslations(m, 'message');
                        });
                    }
                    return t;
                });

                const ticketsNeedingProductInfo = ticketsWithTranslations.filter(
                    (t) => t.product_id && !t.product_type
                );

                if (ticketsNeedingProductInfo.length === 0) {
                    return of(ticketsWithTranslations);
                }

                const uniqueProductIds = [...new Set(ticketsNeedingProductInfo.map((t) => t.product_id))];
                const productRequests = uniqueProductIds.map((productId) =>
                    this.productsService.fetchProductById(productId).pipe(catchError(() => of(null)))
                );

                return forkJoin(productRequests).pipe(
                    map((products) => {
                        const productMap = this.createProductMap(products, uniqueProductIds);
                        return this.enhanceTicketsWithProductTypes(ticketsWithTranslations, productMap);
                    })
                );
            })
        );
    }

    fetchActiveTickets(params?: { includeTicketsExpiredInGivenHours?: number }): Observable<Ticket[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/active`;

        return this.http
            .get(url, {
                params: {
                    ...(this.env.FILTER_PURCHASES_BY_CHANNEL && {
                        channel: this.getChannel()
                    }),
                    ...(params || {})
                }
            })
            .pipe(
                map((d: Ticket[]) => {
                    return d.map((t: Ticket) => {
                        if (t.trip_messages) {
                            t.trip_messages.forEach((m) => {
                                m.translations = [];
                                m.translations = this.dataTransformer.transformObjectTranslations(m, 'message');
                            });
                        }
                        return t;
                    });
                })
            );
    }

    isTransferTicketEmailReceiverCustomer(
        payload: TransferTicketEmailReceiverVerify
    ): Observable<HttpResponse<TransferTicketEmailReceiverVerify>> {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/transfer/email/verify`;
        return this.http.post<TransferTicketEmailReceiverVerify>(url, payload, { observe: 'response' });
    }

    transferTicket(identifier: string, docNumber: string, useDeviceToken = false): Observable<any> {
        const url = this.env.IS_NEW_AFC
            ? `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/give`
            : `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/transfer/email`;

        const payload = this.env.IS_NEW_AFC
            ? {
                  identifier: identifier,
                  ticketIds: [docNumber]
              }
            : {
                  email: identifier,
                  doc_num: docNumber
              };

        return this.http.put(url, payload, {
            context: new HttpContext().set(USE_ANONYMOUS_ACCESS_TOKEN, useDeviceToken)
        });
    }

    refundTicket(cardId: string, docNumber: string): Observable<any> {
        const url = `${this.env.CORE_API_URL}/customer/v2/region/${this.env.REGION_ID}/ticket/refund`;

        const payload = {
            card_id: cardId,
            doc_nums: [docNumber]
        };

        return this.http.put(url, payload);
    }
    recallLendTicket(docNumber: string, useDeviceToken = false): Observable<any> {
        let url: string;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${docNumber}/recall`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/${docNumber}/return-lend`;
        }
        return this.http.post(url, null, {
            context: new HttpContext().set(USE_ANONYMOUS_ACCESS_TOKEN, useDeviceToken)
        });
    }

    returnLendTicket(docNumber: string, useDeviceToken = false): Observable<any> {
        let url: string;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${docNumber}/return`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/${docNumber}/return-lend`;
        }
        return this.http.post(url, null, {
            context: new HttpContext().set(USE_ANONYMOUS_ACCESS_TOKEN, useDeviceToken)
        });
    }

    lendTicket(docNumber: string, email: string, validUntil: string | Date, useDeviceToken = false): Observable<any> {
        let url: string;
        let payload: { receiver?: string; email?: string; docNum?: string; validUntil?: string | Date };

        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${docNumber}/lend`;
            payload = {
                receiver: email
            };
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/lend`;
            payload = {
                email: email,
                docNum: docNumber,
                validUntil: validUntil
            };
        }

        return this.http.post(url, payload, {
            context: new HttpContext().set(USE_ANONYMOUS_ACCESS_TOKEN, useDeviceToken)
        });
    }

    transferTickets(deviceToken: string, ticketIds: string[]) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/transfer`;

        return this.http.put(url, { deviceToken, ticketIds });
    }

    getChannel(isHistory?: boolean): string {
        const channels: string[] = [];

        if (this.env.CHANNEL) {
            channels.push(...this.env.CHANNEL.split(','));
        }

        if (this.env.FILTER_PURCHASES_BY_CHANNEL_SELLER) {
            channels.push('seller');
        }

        if (this.env.HISTORY_CHANNEL && isHistory) {
            channels.push(...this.env.HISTORY_CHANNEL.split(','));
        }

        return [...new Set(channels)].join(',');
    }

    private createProductMap(products: any[], productIds: number[]): Map<number, any> {
        const productMap = new Map();
        products.forEach((product, index) => {
            if (product) {
                productMap.set(productIds[index], product);
            }
        });
        return productMap;
    }

    private enhanceTicketsWithProductTypes(tickets: Ticket[], productMap: Map<number, any>): Ticket[] {
        return tickets.map((ticket) => {
            if (ticket.product_id && !ticket.product_type) {
                const product = productMap.get(ticket.product_id);
                if (product) {
                    ticket.product_type = product.product_type;
                }
            }
            return ticket;
        });
    }
}
